import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Clock, TrendingUp, Play, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentEnrollments } from '../../services/elearningStudentService';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

// Fonction utilitaire pour formater le niveau de difficulté
const formatDifficultyLevel = (level: string) => {
  switch (level) {
    case 'beginner':
      return 'Débutant';
    case 'intermediate':
      return 'Intermédiaire';
    case 'advanced':
      return 'Avancé';
    default:
      return 'Inconnu';
  }
};

const StudentCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.id) {
      loadEnrollments(profile.id);
    }
  };

  const loadEnrollments = async (studentId: string) => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      const enrollmentsData = await getStudentEnrollments(studentId, tenant.id);
      setEnrollments(enrollmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger vos cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };



  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'En cours', variant: 'default' as const, color: 'bg-blue-700 text-blue-200' },
      completed: { label: 'Terminé', variant: 'default' as const, color: 'bg-green-700 text-green-200' },
      suspended: { label: 'Suspendu', variant: 'destructive' as const, color: 'bg-red-700 text-red-200' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCardClick = (status: string) => {
    setStatusFilter(status);
  };



  return (
    <StudentLayout
      title="Mes Cours"
      description="Gérez vos formations et suivez votre progression"
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Mes Cours - E-Learning"
        description="Consultez tous vos cours e-learning et suivez votre progression."
        keywords="cours, progression, e-learning, formation"
      />

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total des cours</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{enrollments.length}</p>
                  </div>
                  <BookOpen className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('active')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En cours</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {enrollments.filter(e => e.status === 'active').length}
                    </p>
                  </div>
                  <Play className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('completed')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Terminés</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {enrollments.filter(e => e.status === 'completed').length}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
      </div>

      {/* Liste des cours */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrollments
              .filter(e => statusFilter === '' || e.status === statusFilter)
              .map((enrollment) => (
              <motion.div
                key={enrollment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  {enrollment.course?.thumbnail_url && (
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={enrollment.course.thumbnail_url}
                        alt={enrollment.course.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4">
                        {getStatusBadge(enrollment.status)}
                      </div>
                    </div>
                  )}
                  
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {enrollment.course?.title}
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                      {enrollment.course?.description}
                    </p>

                    {/* Progression */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Progression
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {enrollment.progress_percentage}%
                        </span>
                      </div>
                      <Progress value={enrollment.progress_percentage} className="h-2" />
                    </div>

                    {/* Informations du cours */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{enrollment.course?.duration_hours}h</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="h-4 w-4" />
                        <span>{formatDifficultyLevel(enrollment.course.difficulty_level || 'beginner')}</span>
                      </div>
                      {enrollment.course.instructor && (
                        <div className="flex items-center space-x-1">
                          <BookOpen className="h-4 w-4" />
                          <span>{enrollment.course.instructor.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Date d'inscription */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Inscrit le {formatDate(enrollment.enrolled_at)}
                    </p>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => {
                          // Vérifier si le token d'authentification est présent
                          const token = localStorage.getItem('elearning_student_token');
                          if (!token) {
                            toast({
                              title: 'Authentification requise',
                              description: 'Veuillez vous connecter pour accéder à ce cours',
                              variant: 'destructive'
                            });
                            navigate('/elearning/auth');
                            return;
                          }
                          
                          // Utiliser la route correcte du lecteur de cours
                          navigate(`/elearning/player/${enrollment.id}`);
                        }}
                        className="flex-1"
                        disabled={enrollment.status === 'suspended'}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {enrollment.progress_percentage === 0 ? 'Commencer' : enrollment.status === 'completed' ? 'Revoir' : 'Continuer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
      </div>

      {/* Message si aucun cours */}
      {enrollments.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Aucun cours trouvé
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Vous n'êtes inscrit à aucun cours pour le moment.
          </p>
          <Button onClick={() => navigate('/elearning/courses')}>
            Découvrir les cours
          </Button>
        </motion.div>
      )}
    </StudentLayout>
  );
};

export default StudentCoursesPage;
